import { Controller } from '@hotwired/stimulus';
import generateUUID from '../src/react/helpers/generateUUID';
import {
  batchSetNodeKVPair,
  createNewContinueButtonInSection,
  DISPLAY_MODES,
  executeActionsInDom,
  filterActionIfAlreadyExistsInDus,
  getDusFromDom,
  syncAllContinueButtonsForDocument,
  unstickLastVisibleContinueButton,
} from '../utils/case_experience_in_rails';
import { CONTINUE_DISABLED_DOCUMENT_KINDS } from '../utils/case_experience/case_experience_api';
import { normalizeNodeUid } from '../utils/case_experience/normalize_node_uid';

export default class extends Controller {
  static targets = ['section', 'continueButton'];
  static values = {
    documentId: Number,
    userId: Number,
    versionId: Number,
    aiEnabled: { type: Boolean, default: false },
    vpeEnabled: { type: Boolean, default: false },
    aiFallbackCards: { type: Boolean, default: false },
    documentKind: String,
    documentUserState: Object,
    isStudent: { type: Boolean, default: true },
  };

  continueButtonsExistingState = {};
  fullViewCollapsedNodeUids = null;
  /** Uids for which we already sent invisible:false after AI fallback reveal (DUS attr may lag DOM). */
  aiFallbackRevealContinuePersistedBtnUids = new Set();

  connect() {
    this.initializeScrollTracking();
    this.loadScrollPosition();

    // Illness scripts and student resources do NOT use continue buttons, but still need AI assistant button handling and fallback reveal logic.
    if (!CONTINUE_DISABLED_DOCUMENT_KINDS.includes(this.documentKindValue)) {
      // For all document kinds that use continue buttons:
      // 1. Add continue buttons to sections if needed (asynchronously).
      // 2. Adjust continue button behavior when AI assistant cards are present.
      // 3. Reveal traditional continue buttons if AI fallback mode is triggered.
      Promise.resolve(this.addContinueButtonsIfNeeded()).then(() => {
        this.adjustContinueButtonFunctionalityForAiAssistantCards();
        return this.revealTraditionalContinueButtonsWhenAiFallback();
      });
    } else {
      // For illness_script and student_resource kinds:
      // - Skip continue button creation.
      // - Still adjust and reveal traditional continue buttons for AI assistant/fallback logic.
      this.adjustContinueButtonFunctionalityForAiAssistantCards();
      void this.revealTraditionalContinueButtonsWhenAiFallback();
    }
  }

  // Loop through all sections and check if they need continue button i.e. if they are before feedback card section
  async addContinueButtonsIfNeeded() {
    let actions = [];
    for (const section of this.sectionTargets) {
      const isFeedbackCard = section.classList.contains('feedback_card');
      const isFinishCard = section.classList.contains('finish_case_card');
      const isReleaseNotesCard = section.classList.contains('release_notes_card');

      if (isFeedbackCard || isFinishCard || isReleaseNotesCard) {
        // Stop processing further sections after feedback, finish or release notes card
        break;
      }

      actions.push(
        createNewContinueButtonInSection(section, this.documentIdValue, this.userIdValue),
      );
    }

    actions = actions.flat();
    actions = filterActionIfAlreadyExistsInDus(actions, this.documentUserStateValue);

    const response = await batchSetNodeKVPair(actions, this.documentIdValue, this.userIdValue);
    if (response && this.application) executeActionsInDom(actions, this.application);

    // After continue buttons are created, apply invisible/inactive properties from documentUserState
    if (this.documentUserStateValue && this.continueButtonTargets) {
      this.continueButtonTargets.forEach((button) => {
        const btnUid = button.id;
        const dus = this.documentUserStateValue[btnUid];
        if (dus) {
          // Find the nearest section "card" for this button (section is a root node for a "continue" button)
          const card = button.closest('[data-case-viewer-target="section"]');
          // Get the section UID that is set as the "landing" section for AI fallback continue button reveal
          const landingUid = this.aiFallbackRevealLandingSectionUid();
          // Compute the normalized UID of the current card (section)
          const cardUid = card?.dataset?.uid ? normalizeNodeUid(card.dataset.uid) : null;
          // Determine if we should skip setting this continue button as invisible based on AI fallback rules:
          // - AI fallback cards are active
          // - The landing UID matches this section
          // - This section is not an AI card (aiDisplayFlag !== 'aiOn')
          const skipInvisibleFromDus =
            this.aiFallbackCardsValue &&
            landingUid &&
            cardUid === landingUid &&
            card?.dataset?.aiDisplayFlag !== 'aiOn';
          // Apply invisible property
          // Determine whether to show or hide the continue button based on the user's document user state (DUS)
          // Case 1: If 'invisible' is true AND this button is NOT the eligible AI fallback reveal landing button → hide button
          if (dus.invisible && dus.invisible.value === true && !skipInvisibleFromDus) {
            button.style.display = 'none';
          // Case 2: If 'invisible' is explicitly false → ensure the button is visible
          } else if (dus.invisible && dus.invisible.value === false) {
            button.style.display = '';
          // Case 3: If this is the AI fallback reveal landing section's continue button and DUS says invisible=true,
          //         we want to override and show the button as visible
          } else if (skipInvisibleFromDus && dus.invisible?.value === true) {
            button.style.display = '';
          }
          // Apply inactive property (2 = disabled, 1 = enabled)
          if (dus.inactive && dus.inactive.value === 2) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('inactive');
          } else if (dus.inactive && dus.inactive.value === 1) {
            button.disabled = false;
            button.setAttribute('aria-disabled', 'false');
            button.classList.remove('inactive');
          }
        }
      });
    }

    await unstickLastVisibleContinueButton(this.application);
    syncAllContinueButtonsForDocument(getDusFromDom());
    await this.revealTraditionalContinueButtonsWhenAiFallback();
  }

  /**
   * Returns the normalized UID of the section to reveal the "continue" button on
   * when AI fallback (AI card failure or fallback) is active. This UID is stored
   * in the document user state under root.aiFallbackRevealContinueSectionUid.
   *
   * If the value is blank or not present, returns null.
   */
  aiFallbackRevealLandingSectionUid() {
    // Get the root of the document user state (should be an object)
    const root = this.documentUserStateValue?.root;
    if (!root || typeof root !== 'object') return null;

    // aiFallbackRevealContinueSectionUid may be camelCase or string-keyed
    const entry = root.aiFallbackRevealContinueSectionUid ?? root['aiFallbackRevealContinueSectionUid'];
    // .value property may be an attribute, or string-keyed
    const raw = entry?.value ?? entry?.['value'];
    // If no UID is set, return null
    if (raw === undefined || raw === null || raw === '') return null;

    // Return normalized UID string for reliable comparison
    return normalizeNodeUid(String(raw));
  }

  /**
   * Reveals the "traditional" continue buttons for the landing section if an AI fallback is active.
   * This is used when the AI card fails, and the experience needs to revert to the standard rails continue UI.
   */
  async revealTraditionalContinueButtonsWhenAiFallback() {
    // Only proceed if AI fallback mode is active and the controller has access to the Stimulus application
    if (!this.aiFallbackCardsValue || !this.application) return;

    // Determine which section (by UID) should reveal the continue button
    const landingUid = this.aiFallbackRevealLandingSectionUid();
    if (!landingUid) return;

    // Get the current document user state (section/button visibility, etc.)
    const dus = this.documentUserStateValue || {};

    // Will accumulate changes for batch persistence
    const actions = [];
    // Keeps track of which button UIDs we plan to operate on (avoid duplication)
    const pendingBtnUids = new Set();

    this.continueButtonTargets.forEach((slice) => {
      // Get the containing section DOM element
      const card = slice.closest('[data-case-viewer-target="section"]');
      // Extract and normalize the section UID
      const cardUid = card?.dataset?.uid ? normalizeNodeUid(card.dataset.uid) : null;

      // Only process if this button belongs to the "landing" section
      if (cardUid !== landingUid) return;

      // Ignore if this section's AI display status is "aiOn" (we only reveal for traditional/rails sections)
      if (card?.dataset?.aiDisplayFlag === 'aiOn') return;

      // Attempt to get the button's UID from various sources (dataset, id, or explicit attribute)
      const btnUidRaw =
        slice.dataset?.uid ||
        slice.id ||
        slice.getAttribute('data-button-action-uid-value');
      const btnUid = btnUidRaw ? normalizeNodeUid(String(btnUidRaw)) : null;
      if (!btnUid) return;

      // Make the continue button visible in the DOM
      slice.setAttribute('data-button-action-invisible-value', 'false');
      slice.style.display = '';

      // Re-run any state update logic on the button's controller, if present
      const ctrl = this.application.getControllerForElementAndIdentifier(slice, 'button-action');
      if (ctrl?.updateButtonState) ctrl.updateButtonState();

      // If the button's invisible state in the user state is already false, nothing to do
      if (dus[btnUid]?.invisible?.value === false) return;
      // If we've already persisted a reveal update for this UID, skip
      if (this.aiFallbackRevealContinuePersistedBtnUids.has(btnUid)) return;
      // If we've already scheduled an update in this batch, skip
      if (pendingBtnUids.has(btnUid)) return;

      pendingBtnUids.add(btnUid);

      // Push actions to un-hide and enable the button (inactive=1 means enabled)
      actions.push(
        { uid: generateUUID(), action: 'setNodeKVPair', arguments: { uid: btnUid, key: 'invisible', value: false } },
        { uid: generateUUID(), action: 'setNodeKVPair', arguments: { uid: btnUid, key: 'inactive', value: 1 } },
      );
    });

    // Nothing to persist? Exit early
    if (actions.length === 0) return;

    try {
      // Persist changes in batch (un-hide/enable all scheduled continue buttons)
      await batchSetNodeKVPair(actions, this.documentIdValue, this.userIdValue);

      // Track which UIDs we've persisted so we don't resend redundant requests
      pendingBtnUids.forEach((uid) => this.aiFallbackRevealContinuePersistedBtnUids.add(uid));
    } catch {
      // Swallow persistence errors (silently fail, safe in most UX cases)
      void 0;
    }
  }

  disconnect() {
    this.saveScrollPosition();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Called by Stimulus when the aiFallbackCardsValue changes.
   * Adjusts continue button logic and reveals traditional (non-AI) continue buttons if fallback mode is active.
   */
  aiFallbackCardsValueChanged() {
    this.adjustContinueButtonFunctionalityForAiAssistantCards();
    // Ensure continue buttons are revealed in fallback cards state
    void this.revealTraditionalContinueButtonsWhenAiFallback();
  }

  /**
   * Stimulus callback: called when documentUserStateValue changes (i.e. when user state is rehydrated/updated).
   * Reveals traditional continue buttons if AI fallback is active.
   */
  documentUserStateValueChanged() {
    // Ensure traditional continue buttons are revealed if fallback is active after state change
    void this.revealTraditionalContinueButtonsWhenAiFallback();
  }

  adjustContinueButtonFunctionalityForAiAssistantCards() {
    if (!this.aiEnabledValue || !this.vpeEnabledValue || this.aiFallbackCardsValue) {
      if (this.aiFallbackCardsValue) {
        this.restoreAllContinueButtonsToOriginal();
      }
      return;
    }

    this.continueButtonTargets.forEach((button) => {
      const section = button.closest('[data-case-viewer-target="section"]');
      if (!section) return;
      const siblingSection = this.getNextSectionSibling(section);

      const onClickAttr = button.getAttribute('data-button-action-on-click-value');
      if (!onClickAttr) {
        // If there is no on-click configuration, skip this button.
        return;
      }
      let existingOnClick;
      try {
        existingOnClick = JSON.parse(onClickAttr);
      } catch (e) {
        // If the JSON is invalid, log the error and skip processing this button to avoid runtime errors.
        console.error(
          'Failed to parse data-button-action-on-click-value JSON for continue button.',
          {
            buttonId: button.id,
            attributeValue: onClickAttr,
            error: e,
          },
        );
        return;
      }

      this.continueButtonsExistingState[button.id] = {
        onclick: existingOnClick,
      };

      // Walk past ai-pair counterparts so buttons inside one half of a pair
      // don't get rewritten (aiOff buttons skip aiOn siblings and vice versa).
      const sectionFlag = section?.dataset.aiDisplayFlag;
      const oppositeFlag = sectionFlag === 'aiOff' ? 'aiOn' : sectionFlag === 'aiOn' ? 'aiOff' : null;
      if (oppositeFlag) {
        let walker = this.getNextSectionSibling(section);
        while (walker && walker.dataset.aiDisplayFlag === oppositeFlag) {
          walker = this.getNextSectionSibling(walker);
        }
        if (walker && walker !== siblingSection) return;
      }

      // Resolve effective sibling: when current section is not itself part of an
      // aiOn/aiOff pair (e.g. unset flag or alwaysOn), walk past any consecutive
      // aiOff sections so the remap targets the first actually-visible successor.
      let effectiveSibling = siblingSection;
      if (
        sectionFlag !== 'aiOn' &&
        sectionFlag !== 'aiOff' &&
        effectiveSibling?.dataset.aiDisplayFlag === 'aiOff'
      ) {
        while (effectiveSibling && effectiveSibling.dataset.aiDisplayFlag === 'aiOff') {
          effectiveSibling = this.getNextSectionSibling(effectiveSibling);
        }
      }

      if (effectiveSibling && effectiveSibling.dataset.aiDisplayFlag !== 'aiOff') {
        if (!button.hasAttribute('data-original-button-action-on-click-value')) {
          button.setAttribute(
            'data-original-button-action-on-click-value',
            JSON.stringify(existingOnClick),
          );
        }
        effectiveSibling.setAttribute('data-existing-on-click', JSON.stringify(existingOnClick));

        // Get the node uid of the sibling section
        const targetNodeUid = effectiveSibling.id;

        // Deep clone existingOnClick to avoid mutating the original
        let clonedOnClick;
        if (typeof structuredClone === 'function') {
          clonedOnClick = structuredClone(existingOnClick);
        } else {
          clonedOnClick = JSON.parse(JSON.stringify(existingOnClick));
        }

        // Replace invisible & collapsed with value false type entries's uid with the targetNodeUid
        const newOnClick = clonedOnClick.map((entry) => {
          const isInvisibleOrCollapsedEntry =
            entry.type === 'action' &&
            entry.action === 'setNodeKVPair' &&
            entry.arguments &&
            entry.arguments.key &&
            (entry.arguments.key === 'invisible' || entry.arguments.key === 'collapsed') &&
            entry.arguments.value === false;

          if (isInvisibleOrCollapsedEntry && !!entry?.arguments?.uid) {
            entry.arguments.uid = targetNodeUid;
          }

          return entry;
        });
        button.setAttribute('data-button-action-on-click-value', JSON.stringify(newOnClick));
        effectiveSibling.setAttribute('data-new-on-click', JSON.stringify(newOnClick));
      }

      if (!effectiveSibling || effectiveSibling.dataset.aiDisplayFlag === 'aiOff') {
        // Restore original functionality if it was modified
        if (this.continueButtonsExistingState[button.id]) {
          const originalState = this.continueButtonsExistingState[button.id];
          button.setAttribute(
            'data-button-action-on-click-value',
            JSON.stringify(originalState.onclick),
          );
          section.removeAttribute('data-existing-on-click');
          section.removeAttribute('data-new-on-click');
        }
      }
    });
  }

  restoreAllContinueButtonsToOriginal() {
    this.continueButtonTargets.forEach((button) => {
      const originalOnClick = button.getAttribute('data-original-button-action-on-click-value');
      if (originalOnClick) {
        button.setAttribute('data-button-action-on-click-value', originalOnClick);
        button.removeAttribute('data-original-button-action-on-click-value');
        const section = button.closest('[data-case-viewer-target="section"]');
        if (section) {
          section.removeAttribute('data-existing-on-click');
          section.removeAttribute('data-new-on-click');
        }
      }
    });
    this.continueButtonsExistingState = {};
  }

  getNextSectionSibling(currentSection) {
    if (!currentSection) return null;
    let nextSibling = currentSection.nextElementSibling;

    while (nextSibling) {
      if (nextSibling.matches('[data-case-viewer-target="section"]')) {
        return nextSibling;
      }
      nextSibling = nextSibling.nextElementSibling;
    }

    return null;
  }

  initializeScrollTracking() {
    // Track which sections have been viewed
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.markSectionAsVisited(entry.target);
          }
        });
      },
      {
        threshold: 0.5, // Section is considered viewed when 50% visible
        rootMargin: '0px',
      },
    );

    this.sectionTargets.forEach((section) => {
      this.observer.observe(section);
    });
  }

  markSectionAsVisited(section) {
    const nodeUid = section.dataset.nodeUid;
    if (!nodeUid) return;

    section.classList.add('visited');

    // Dispatch custom event for progress tracking
    this.dispatch('sectionVisited', {
      detail: { nodeUid: nodeUid },
    });
  }

  saveScrollPosition() {
    const scrollPosition = window.scrollY;
    sessionStorage.setItem(`case_scroll_${this.documentIdValue}`, scrollPosition);
  }

  loadScrollPosition() {
    const scrollPosition = sessionStorage.getItem(`case_scroll_${this.documentIdValue}`);
    if (scrollPosition !== null && scrollPosition !== '') {
      const parsed = parseInt(scrollPosition, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        window.scrollTo(0, parsed);
      }
    }
  }

  scrollToNode(event) {
    const nodeUid = event.detail?.nodeUid || event.params?.nodeUid;
    if (!nodeUid) return;

    const section = this.sectionTargets.find((el) => el.dataset.nodeUid === nodeUid);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  changeViewMode(event) {
    const mode = event.target.value;
    if (!DISPLAY_MODES.includes(mode)) return;

    const docBody =
      event.target.closest('.doc-body') ||
      this.element.closest('.doc-body') ||
      this.element.querySelector('.doc-body');
    if (!docBody) return;

    const previousMode = DISPLAY_MODES.find((m) =>
      docBody.classList.contains(`${m}-display-mode`),
    );

    DISPLAY_MODES.forEach((m) => docBody.classList.remove(`${m}-display-mode`));
    docBody.classList.add(`${mode}-display-mode`);

    if (!this.isStudentValue) {
      const expandedModes = new Set(['full', 'outline']);
      const wasExpandedMode = expandedModes.has(previousMode);
      const isExpandedMode = expandedModes.has(mode);

      if (!wasExpandedMode && isExpandedMode) {
        this.expandSectionsForAdminFullView(docBody);
      }
      if (wasExpandedMode && !isExpandedMode) {
        this.restoreFullViewCollapsedSnapshot(docBody);
      }
    }
  }

  eachSectionCollapseControllerIn(docBody, fn) {
    for (const el of docBody.querySelectorAll('[data-controller~="section-collapse"]')) {
      const c = this.application.getControllerForElementAndIdentifier(el, 'section-collapse');
      if (c) fn(c, el);
    }
  }

  expandSectionsForAdminFullView(docBody) {
    const snapshot = (this.fullViewCollapsedNodeUids ??= new Set());
    snapshot.clear();
    this.eachSectionCollapseControllerIn(docBody, (c, el) => {
      if (c.stateValue?.collapsed !== true) return;
      const uid = c.nodeUidValue || el.dataset.uid;
      if (uid) snapshot.add(uid);
      c.stateValue = { ...c.stateValue, collapsed: false };
      c.updateUI();
    });
  }

  restoreFullViewCollapsedSnapshot(docBody) {
    const snapshot = this.fullViewCollapsedNodeUids;
    if (!snapshot?.size) return;
    this.eachSectionCollapseControllerIn(docBody, (c, el) => {
      const uid = c.nodeUidValue || el.dataset.uid;
      if (!uid || !snapshot.has(uid)) return;
      c.stateValue = { ...c.stateValue, collapsed: true };
      c.updateUI();
      c.stopTimeTracking();
    });
    snapshot.clear();
  }
}
